<template>
  <div class="vat-report">
    <header class="report-hero">
      <div>
        <div class="eyebrow">Контроль интеграции Master ↔ УСП</div>
        <h1>НДС по году подписания АВР</h1>
        <p>
          Сверка СМР подрядчика в дампе УСП с четырьмя методиками по подписанному АВР.
          ТМЦ команды в дампе — отдельная справка, в дельту НДС не входит.
        </p>
      </div>
      <div class="hero-actions">
        <q-btn
          outline
          no-caps
          color="primary"
          icon="help_outline"
          label="Как читать отчёт"
          class="help-btn"
          @click="helpOpen = true"
        />
        <q-btn
          unelevated
          no-caps
          color="primary"
          icon="refresh"
          label="Обновить"
          :loading="loading"
          @click="loadReport"
        />
      </div>
    </header>

    <section class="filters">
      <q-select
        v-model="filters.complex"
        outlined
        dense
        clearable
        emit-value
        map-options
        :options="complexOptions"
        label="ЖК"
        class="filter-control"
      />
      <q-select
        v-model="filters.status"
        outlined
        dense
        emit-value
        map-options
        :options="statusOptions"
        label="Результат сравнения"
        class="filter-control"
      />
      <q-input
        v-model.trim="filters.search"
        outlined
        dense
        clearable
        debounce="250"
        label="Проект, договор, GUID"
        class="filter-search"
      >
        <template #prepend><q-icon name="search" /></template>
      </q-input>
      <div class="filter-toggle">
        <q-toggle
          v-model="filters.onlyCases"
          color="negative"
          label="Скрыть совпадающие ЖК"
        />
        <div class="filter-toggle-hint">
          Прячет ЖК со статусом «Совпадает». Счётчики сверху считают все ЖК выборки.
        </div>
      </div>
    </section>

    <q-linear-progress v-if="loading" indeterminate color="primary" />

    <q-banner v-if="errorText" rounded class="error-banner">
      {{ errorText }}
      <template #action>
        <q-btn flat no-caps label="Повторить" @click="loadReport" />
      </template>
    </q-banner>

    <section v-if="!loading && rows.length" class="summary-grid">
      <article class="summary-card">
        <span>ЖК в выборке</span><strong>{{ summary.complexes }}</strong>
      </article>
      <article class="summary-card">
        <span>Проектов с АВР</span><strong>{{ summary.projects }}</strong>
      </article>
      <article class="summary-card summary-card--danger">
        <span>Расхождения &gt; {{ money(tolerance) }} ₸</span>
        <strong>{{ summary.differences }}</strong>
      </article>
      <article class="summary-card summary-card--warning">
        <span>Требуют проверки</span><strong>{{ summary.review }}</strong>
      </article>
      <article class="summary-card summary-card--ok">
        <span>Совпадает</span><strong>{{ summary.matches }}</strong>
      </article>
    </section>

    <div v-if="!loading && rows.length" class="result-caption">
      <div>Показано ЖК: {{ filteredGroups.length }} из {{ groups.length }}</div>
      <div v-if="hiddenMatchCount" class="result-caption-note">
        Скрыто {{ hiddenMatchCount }} ЖК со статусом «Совпадает» —
        факт СМР в дампе совпал с методом по году АВР.
        Выключите «Скрыть совпадающие ЖК», чтобы увидеть их в списке.
      </div>
      <div v-if="hiddenOtherCount" class="result-caption-note">
        Ещё скрыто {{ hiddenOtherCount }} по выбранным фильтрам (ЖК, статус или поиск).
      </div>
    </div>

    <section class="complex-list">
      <q-expansion-item
        v-for="group in filteredGroups"
        :key="group.project_guid"
        expand-separator
        class="complex-card"
      >
        <template #header>
          <q-item-section>
            <div class="complex-title-row">
              <div>
                <div class="complex-title">{{ group.resident_name }}</div>
                <div class="complex-subtitle">
                  {{ group.usp_project_name || "Проект УСП без названия" }}
                  · SBS {{ group.sbs_project_id }}
                </div>
              </div>
              <status-badge :status="group.complex_comparison_status" />
            </div>
          </q-item-section>
        </template>

        <div class="complex-body">
          <div class="guid-row">
            <span>ProjectGuid</span>
            <code>{{ group.project_guid }}</code>
            <q-btn
              v-if="group.project_guid"
              flat
              dense
              round
              size="sm"
              color="primary"
              icon="content_copy"
              class="guid-copy-btn"
              @click="copyText(group.project_guid)"
            >
              <q-tooltip>Копировать ProjectGuid</q-tooltip>
            </q-btn>
            <q-btn
              v-if="group.api_url"
              unelevated
              no-caps
              dense
              color="primary"
              icon="link"
              label="Эндпоинт УСП"
              class="endpoint-btn"
            >
              <q-menu
                anchor="bottom left"
                self="top left"
                :offset="[0, 8]"
                max-width="560px"
              >
                <div class="endpoint-popup">
                  <div class="endpoint-popup-label">Адрес УСП этого ЖК</div>
                  <code>{{ group.api_url }}</code>
                  <div class="endpoint-popup-actions">
                    <q-btn
                      flat
                      dense
                      no-caps
                      color="primary"
                      icon="content_copy"
                      label="Копировать"
                      @click="copyText(group.api_url)"
                    />
                    <q-btn
                      flat
                      dense
                      no-caps
                      color="primary"
                      icon="open_in_new"
                      label="Открыть"
                      :href="group.api_url"
                      target="_blank"
                    />
                  </div>
                </div>
              </q-menu>
            </q-btn>
          </div>
          <div v-if="group.has_out_of_scope_share" class="project-note">
            <q-icon name="warning" />
            У ЖК есть общий WorkGuid с проектом вне отчёта. Дамп нельзя честно
            сравнить только с АВР строк START_SMR/COMPLETED.
          </div>

          <div class="amount-grid">
            <amount-tile
              label="Факт СМР в дампе"
              :value="group.usp_fact_complex"
              tone="actual"
              hint="FactValue по уникальным WorkGuid ЖК: факт подрядчика в GetSBSData. Без ТМЦ команды."
            />
            <amount-tile
              label="Метод по году АВР"
              :value="group.complex_avr_year_target"
              tone="target"
              hint="Сумма подписанных АВР подрядчика: до 2026 делится на 1.12, с 2026 — на 1.16. ТМЦ команды в АВР нет."
            />
            <amount-tile
              label="Дельта СМР"
              :value="group.fact_complex_minus_avr_year"
              :tone="deltaTone(group.fact_complex_minus_avr_year)"
              signed
              hint="Факт СМР в дампе − метод по году АВР. Статусы MATCH / DIFFERENCE считаются по этой дельте."
            />
            <amount-tile
              label="Смета СМР в дампе"
              :value="group.usp_smeta_complex"
              hint="SmetaValue подрядчика. Справка: сверка и статусы идут по факту СМР, не по смете."
            />
          </div>
          <div class="tmc-strip">
            <amount-tile
              label="Факт ТМЦ команды (справка)"
              :value="group.usp_fact_tmc_complex"
              hint="FactMaterialValue в дампе УСП — ТМЦ команды проекта. В подписанный АВР подрядчика не входит и в дельту НДС не идёт."
            />
            <amount-tile
              label="Смета ТМЦ команды (справка)"
              :value="group.usp_smeta_tmc_complex"
              hint="SmetaMaterialValue в дампе УСП. Справка, без сверки с АВР."
            />
          </div>

          <div class="method-strip">
            <div>
              <span>Исторические ставки строк</span>
              <strong>{{ money(group.complex_historical_rate_target) }} ₸</strong>
              <q-tooltip max-width="340px">
                Каждый подписанный АВР ЖК пересчитан по ставке НДС, которая стояла
                на строке работы и материала в момент АВР: сумма / (1 + ставка / 100).
              </q-tooltip>
            </div>
            <div>
              <span>Единый НДС 12%</span>
              <strong>{{ money(group.complex_uniform_12_target) }} ₸</strong>
              <q-tooltip max-width="340px">
                Все подписанные АВР ЖК делятся на 1.12, без учёта года и ставки строки.
              </q-tooltip>
            </div>
            <div>
              <span>Единый НДС 16%</span>
              <strong>{{ money(group.complex_uniform_16_target) }} ₸</strong>
              <q-tooltip max-width="340px">
                Все подписанные АВР ЖК делятся на 1.16, без учёта года и ставки строки.
              </q-tooltip>
            </div>
            <div class="closest-method">
              <span>Ближайшая методика (по факту СМР)</span>
              <strong>{{ methodLabel(group.closest_complex_method) }}</strong>
              <q-tooltip max-width="340px">
                Какая из четырёх методик ближе к факту СМР в дампе. Это справка,
                а не вердикт, какая сторона права.
              </q-tooltip>
            </div>
          </div>

          <section v-if="coverageSummary(group)" class="coverage-block">
            <div class="coverage-head">
              <div>
                <div class="coverage-title">WorkGuid ЖК</div>
                <div class="coverage-sub">
                  Карта ключей, не сверка НДС. Суммы в срезах — факт СМР подрядчика.
                  ТМЦ команды в эти цифры не входит.
                </div>
              </div>
            </div>
            <div class="coverage-stats">
              <button
                type="button"
                class="coverage-stat"
                :class="{ 'is-active': coverageTabOf(group) === 'all' }"
                @click="setCoverageTab(group, 'all')"
              >
                <span>Всего ключей</span>
                <strong>{{ coverageSummary(group).key_count || 0 }}</strong>
              </button>
              <button
                type="button"
                class="coverage-stat coverage-stat--warn"
                :class="{ 'is-active': coverageTabOf(group) === 'holes' }"
                @click="setCoverageTab(group, 'holes')"
              >
                <span>В дампе, без АВР (СМР)</span>
                <strong>{{ coverageSummary(group).dump_without_avr_count || 0 }}</strong>
                <em>{{ money(coverageSummary(group).fact_dump_without_avr) }} ₸</em>
              </button>
              <button
                type="button"
                class="coverage-stat"
                :class="{ 'is-active': coverageTabOf(group) === 'ok' }"
                @click="setCoverageTab(group, 'ok')"
              >
                <span>В дампе и с АВР (СМР)</span>
                <strong>{{ coverageSummary(group).with_avr_count || 0 }}</strong>
                <em>{{ money(coverageSummary(group).fact_dump_with_avr) }} ₸</em>
              </button>
              <button
                type="button"
                class="coverage-stat"
                :class="{ 'is-active': coverageTabOf(group) === 'no_dump' }"
                @click="setCoverageTab(group, 'no_dump')"
              >
                <span>АВР, нет в дампе</span>
                <strong>{{ coverageSummary(group).avr_without_dump_count || 0 }}</strong>
              </button>
              <button
                type="button"
                class="coverage-stat"
                :class="{ 'is-active': coverageTabOf(group) === 'shared' }"
                @click="setCoverageTab(group, 'shared')"
              >
                <span>Общие ключи</span>
                <strong>{{ coverageSummary(group).shared_count || 0 }}</strong>
              </button>
            </div>
            <div class="coverage-tmc-note">
              Факт ТМЦ команды в дампе:
              <b>{{ money(coverageSummary(group).tmc_in_dump) }} ₸</b>
              — справка, в суммы срезов и в дельту НДС не входит.
            </div>
            <q-input
              :model-value="coverageSearchOf(group)"
              outlined
              dense
              clearable
              debounce="200"
              label="Найти WorkGuid или project_id"
              class="coverage-search"
              @update:model-value="setCoverageSearch(group, $event)"
            >
              <template #prepend><q-icon name="search" /></template>
            </q-input>
            <div class="coverage-list">
              <article
                v-for="item in visibleCoverage(group)"
                :key="item.work_guid"
                class="coverage-row"
              >
                <div class="coverage-guid">
                  <code>{{ item.work_guid }}</code>
                  <q-btn
                    flat
                    dense
                    round
                    size="sm"
                    color="primary"
                    icon="content_copy"
                    @click="copyText(item.work_guid)"
                  >
                    <q-tooltip>Копировать WorkGuid</q-tooltip>
                  </q-btn>
                </div>
                <div class="coverage-flags">
                  <q-badge :color="item.in_dump ? 'primary' : 'grey-6'">
                    {{ item.in_dump ? "Есть в дампе" : "Нет в дампе" }}
                  </q-badge>
                  <q-badge :color="item.has_signed_avr ? 'positive' : 'warning'">
                    {{ item.has_signed_avr ? "Есть подписанный АВР" : "Нет подписанного АВР" }}
                  </q-badge>
                  <q-badge v-if="item.is_shared" color="indigo">Общий ключ</q-badge>
                  <q-badge v-if="item.has_out_of_scope_share" color="orange">
                    Сосед вне отчёта
                  </q-badge>
                </div>
                <div class="coverage-meta">
                  <span v-if="item.in_dump">
                    СМР: {{ money(item.usp_fact) }} ₸
                  </span>
                  <span v-if="item.in_dump">
                    ТМЦ команды: {{ money(item.usp_fact_tmc) }} ₸
                  </span>
                  <span v-if="item.project_ids && item.project_ids.length">
                    Проекты: {{ item.project_ids.join(", ") }}
                    <template v-if="item.project_statuses && item.project_statuses.length">
                      ({{ item.project_statuses.join(", ") }})
                    </template>
                  </span>
                  <span v-if="item.avr_project_ids && item.avr_project_ids.length">
                    АВР на проектах: {{ item.avr_project_ids.join(", ") }}
                  </span>
                </div>
              </article>
              <div v-if="!visibleCoverage(group).length" class="coverage-empty">
                <template v-if="coverageSearchOf(group)">
                  По запросу «{{ coverageSearchOf(group) }}» ничего нет.
                  <span v-if="coverageTabOf(group) !== 'all'">
                    Сейчас открыт срез, не все ключи. Нажмите «Всего ключей».
                  </span>
                  <span v-else>
                    Введите project_id целиком (например 649) или кусок WorkGuid.
                  </span>
                </template>
                <template v-else>
                  Нет ключей в этом срезе
                </template>
              </div>
            </div>
            <div
              v-if="filteredCoverage(group).length > visibleCoverage(group).length"
              class="coverage-more"
            >
              Показано {{ visibleCoverage(group).length }} из
              {{ filteredCoverage(group).length }}
              <q-btn
                flat
                dense
                no-caps
                color="primary"
                label="Показать все"
                @click="setCoverageLimit(group, filteredCoverage(group).length)"
              />
            </div>
          </section>

          <div class="project-list">
            <article
              v-for="project in group.projects"
              :key="project.project_id"
              class="project-card"
            >
              <div class="project-header">
                <div>
                  <div class="project-id">
                    Project {{ project.project_id }}
                    <q-badge outline color="grey-8">
                      {{ project.project_status_code }}
                    </q-badge>
                  </div>
                  <div class="contract-line">
                    {{ contractLabel(project) }}
                    <span v-if="project.contractor_name">· {{ project.contractor_name }}</span>
                  </div>
                </div>
                <div class="project-status-col">
                  <status-badge :status="project.project_comparison_status" compact />
                  <div
                    v-if="project.closest_project_method"
                    class="project-closest"
                  >
                    ближе к {{ methodLabel(project.closest_project_method) }}
                  </div>
                </div>
              </div>

              <div class="project-methods">
                <div>
                  <span>Факт СМР дампа</span>
                  <b>{{ nullableMoney(project.usp_fact_project) }}</b>
                  <q-tooltip max-width="340px">
                    FactValue по эксклюзивным WorkGuid проекта, без ТМЦ команды.
                    Если ключи общие — сумма на уровне ЖК, здесь «На уровне ЖК».
                  </q-tooltip>
                </div>
                <div>
                  <span>Исторические</span>
                  <b>{{ money(project.historical_rate_target) }} ₸</b>
                  <q-tooltip max-width="340px">
                    Подписанные АВР проекта, пересчитанные по ставке НДС строки
                    на момент АВР: сумма / (1 + ставка / 100).
                  </q-tooltip>
                </div>
                <div>
                  <span>Единый 12%</span>
                  <b>{{ money(project.uniform_12_target) }} ₸</b>
                  <q-tooltip max-width="340px">
                    Сумма подписанных АВР проекта / 1.12.
                  </q-tooltip>
                </div>
                <div>
                  <span>Единый 16%</span>
                  <b>{{ money(project.uniform_16_target) }} ₸</b>
                  <q-tooltip max-width="340px">
                    Сумма подписанных АВР проекта / 1.16.
                  </q-tooltip>
                </div>
                <div>
                  <span>По году АВР</span>
                  <b>{{ money(project.avr_year_target) }} ₸</b>
                  <q-tooltip max-width="340px">
                    АВР проекта до 2026 / 1.12, с 2026 / 1.16.
                  </q-tooltip>
                </div>
                <div>
                  <span>Факт − год АВР</span>
                  <b :class="deltaClass(project.fact_project_minus_avr_year)">
                    {{ signedNullable(project.fact_project_minus_avr_year) }}
                  </b>
                  <q-tooltip max-width="340px">
                    Факт СМР дампа − метод по году АВР. Если ключи общие,
                    считается только на карточке ЖК.
                  </q-tooltip>
                </div>
              </div>
              <div class="project-tmc">
                <div>
                  <span>Факт ТМЦ команды</span>
                  <b>{{ nullableMoney(project.usp_fact_tmc_project) }}</b>
                  <q-tooltip max-width="340px">
                    FactMaterialValue по эксклюзивным WorkGuid проекта.
                    Справка: в подписанный АВР подрядчика и в дельту НДС не входит.
                  </q-tooltip>
                </div>
                <div>
                  <span>Смета ТМЦ команды</span>
                  <b>{{ nullableMoney(project.usp_smeta_tmc_project) }}</b>
                  <q-tooltip max-width="340px">
                    SmetaMaterialValue по эксклюзивным WorkGuid проекта. Справка.
                  </q-tooltip>
                </div>
              </div>

              <div class="badges">
                <q-badge
                  :color="project.is_full_avr_coverage ? 'positive' : 'warning'"
                  :label="project.is_full_avr_coverage
                    ? `АВР покрывают ключи (${project.signed_avr_count})`
                    : 'Неполное покрытие АВР'"
                />
                <q-badge v-if="project.has_mixed_historical_vat" color="deep-orange">
                  Смешанные ставки: {{ rateLabel(project.historical_vat_rates) }}
                </q-badge>
                <q-badge v-if="project.has_shared_work_guid" color="indigo">
                  Общие WorkGuid: {{ project.shared_work_guid_count }}
                </q-badge>
                <q-badge v-if="project.transfer_event_count" color="blue-grey">
                  Переносы: {{ project.transfer_event_count }}
                </q-badge>
                <q-badge v-if="project.postman_log_count" color="purple">
                  POSTMAN в журнале: {{ project.postman_log_count }}
                </q-badge>
              </div>

              <div
                v-if="project.has_shared_work_guid"
                class="project-note"
              >
                <q-icon name="hub" />
                Сравнение выполняется на уровне ЖК: дамп не содержит PartnerGuid.
                Общий WorkGuid допустим для нового UpdateSmeta.
                <span>{{ sharedProjectsLabel(project.shared_work_guid_details) }}</span>
              </div>
              <div v-if="project.transfer_event_count" class="project-note">
                <q-icon name="swap_horiz" />
                История переносов: {{ dateTime(project.first_transfer_at) }} —
                {{ dateTime(project.last_transfer_at) }}.
                <span v-if="project.transfer_employees">
                  Пользователи: {{ project.transfer_employees }}.
                </span>
              </div>
            </article>
          </div>
        </div>
      </q-expansion-item>
    </section>

    <div v-if="!loading && rows.length && !filteredGroups.length" class="empty-state">
      <q-icon name="filter_alt_off" />
      <div>По выбранным фильтрам ничего не найдено</div>
    </div>
    <div v-if="!loading && !rows.length && !errorText" class="empty-state">
      <q-icon name="receipt_long" />
      <div>Нет проектов с подписанными АВР в выбранном контуре</div>
    </div>

    <q-dialog v-model="helpOpen">
      <q-card class="help-card">
        <q-card-section class="help-card-head">
          <div>
            <div class="help-kicker">Справка</div>
            <div class="help-title">Как читать этот отчёт</div>
          </div>
          <q-btn v-close-popup flat round dense icon="close" />
        </q-card-section>
        <q-separator />
        <q-card-section class="help-card-body">
          <section>
            <h2>Кто попадает в выборку</h2>
            <p>
              В отчёт входят только проекты со статусом START_SMR или COMPLETED,
              у которых есть хотя бы один <b>подписанный АВР</b> (тип BUH_AVR,
              флаг is_signed). Отправка в 1С для этой сверки не требуется.
              Проекты без подписанного АВР, отменённые и только что созданные
              в список не попадают.
            </p>
          </section>
          <section>
            <h2>С чем сравниваем</h2>
            <p>
              Основной показатель — <b>факт СМР подрядчика</b> в дампе УСП
              (FactValue) против метода «по году подписания АВР»: до 2026
              сумма делится на 1.12, с 2026 — на 1.16. Это сравнимо с
              подписанным АВР подрядчика: в avr_sum нет ТМЦ команды.
              Смета СМР и ТМЦ команды (FactMaterialValue / SmetaMaterialValue)
              рядом как справка и в статусы MATCH / DIFFERENCE не входят.
              Совпадение — если разница СМР не больше {{ money(tolerance) }} ₸
              и все текущие WorkGuid проекта покрыты этими АВР.
            </p>
            <p>
              Отчёт не говорит, кто прав: Master или УСП. Он показывает,
              какой способ расчёта ближе к факту СМР в дампе.
            </p>
          </section>
          <section>
            <h2>Статусы</h2>
            <ul>
              <li>
                <b>Расхождение методик</b> — факт СМР в дампе отличается от суммы
                по году АВР больше чем на {{ money(tolerance) }} ₸.
              </li>
              <li>
                <b>Нужна проверка</b> — подписанные АВР не покрывают все текущие
                WorkGuid, либо ключ делят с проектом вне отчёта. Сверку нельзя
                считать подтверждённой.
              </li>
              <li>
                <b>Сверка по ЖК</b> — у проекта есть общие WorkGuid с другими
                объектами. Дамп УСП не содержит PartnerGuid, поэтому сумму
                проекта из дампа нельзя честно разрезать. Смотрите карточку ЖК.
                Общий WorkGuid для нового UpdateSmeta — нормальная схема
                (несколько Partners на один Guid).
              </li>
              <li>
                <b>Совпадает</b> — факт СМР и метод по году АВР сошлись,
                покрытие ключей полное.
              </li>
              <li>
                <b>Нет дампа УСП</b> — в текущем дампе нет сумм по этим ключам.
              </li>
            </ul>
          </section>
          <section>
            <h2>Переключатель «Скрыть совпадающие ЖК»</h2>
            <p>
              Когда он включён, из списка пропадают ЖК со статусом «Совпадает».
              Счётчики «ЖК в выборке» и «Совпадает» по-прежнему считают все ЖК.
              Строка «Показано ЖК: 17 из 18» как раз про это: восемнадцатый
              спрятан переключателем, а не пропал из базы.
            </p>
          </section>
          <section>
            <h2>Фильтры и вкладки</h2>
            <p>
              Выбранные ЖК, статус, поиск и переключатель пишутся в адрес
              страницы. Если открыть ссылку в новой вкладке или скопировать URL,
              фильтры останутся теми же.
            </p>
          </section>
          <section>
            <h2>Список WorkGuid</h2>
            <p>
              Раскрытие на карточке ЖК. Это карта покрытия, не сверка НДС.
              Суммы в срезах — факт СМР подрядчика, без ТМЦ команды.
              «В дампе, без АВР» — СМР уже в УСП, подписанного АВР нет;
              из‑за таких ключей дамп СМР больше метода по году АВР.
              «Сосед вне отчёта» — Guid делят с проектом без подписанного АВР
              или не START_SMR/COMPLETED; дамп по этому Guid нельзя честно
              сравнить только с АВР строк отчёта.
              Сумму дампа на документ АВР не делим: в GetSBSData нет номера АВР.
            </p>
          </section>
          <section>
            <h2>Метки внутри проекта</h2>
            <ul>
              <li>
                <b>АВР покрывают ключи</b> — каждый текущий WorkGuid найден
                в факте, связанном с подписанным АВР.
              </li>
              <li>
                <b>Общие WorkGuid</b> — сравнение на уровне ЖК, не проекта.
              </li>
              <li>
                <b>Переносы</b> — в журнале меняли подъезд или проект у этажа.
              </li>
              <li>
                <b>POSTMAN в журнале</b> — в восстановленных логах есть пометка
                POSTMAN. Штатная отправка из Master такую пометку сама не ставит.
              </li>
            </ul>
          </section>
        </q-card-section>
        <q-separator />
        <q-card-actions align="right">
          <q-btn v-close-popup unelevated no-caps color="primary" label="Понятно" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { fetchUspNdsAvrYearReport } from "@/views/ProjectsEntry/services";
import StatusBadge from "@/views/Reports/components/UspReportStatusBadge.vue";
import AmountTile from "@/views/Reports/components/UspReportAmountTile.vue";

const METHOD_LABELS = {
  AVR_YEAR: "По году АВР",
  HISTORICAL: "Исторические ставки",
  UNIFORM_12: "Единый НДС 12%",
  UNIFORM_16: "Единый НДС 16%",
};

const STATUS_VALUES = ["ALL", "DIFFERENCE", "REVIEW", "COMPLEX_SCOPE", "MATCH", "NO_USP_DUMP"];

export default {
  name: "UspNdsAvrYearReport",
  components: { StatusBadge, AmountTile },
  data() {
    return {
      rows: [],
      meta: {},
      loading: false,
      errorText: "",
      helpOpen: false,
      syncingQuery: false,
      coverageTab: {},
      coverageSearch: {},
      coverageLimit: {},
      filters: {
        complex: null,
        status: "ALL",
        search: "",
        onlyCases: true,
      },
      statusOptions: [
        { label: "Все результаты", value: "ALL" },
        { label: "Расхождение методик", value: "DIFFERENCE" },
        { label: "Нужна проверка", value: "REVIEW" },
        { label: "Сверка по ЖК", value: "COMPLEX_SCOPE" },
        { label: "Совпадает", value: "MATCH" },
        { label: "Нет дампа УСП", value: "NO_USP_DUMP" },
      ],
    };
  },
  computed: {
    tolerance() {
      return Number(this.meta?.tolerance ?? this.rows[0]?.tolerance ?? 1);
    },
    groups() {
      const map = new Map();
      this.rows.forEach((row) => {
        const key = row.project_guid || `sbs-${row.sbs_project_id}`;
        if (!map.has(key)) {
          map.set(key, {
            ...row,
            projects: [],
            work_guid_coverage: [],
            work_guid_coverage_summary: this.parseJsonValue(
              row.work_guid_coverage_summary,
              {},
            ),
          });
        }
        const group = map.get(key);
        group.projects.push(row);
        const details = this.normalizeCoverageList(row.work_guid_coverage);
        if (details.length && !group.work_guid_coverage.length) {
          group.work_guid_coverage = details;
        }
        const summary = this.parseJsonValue(row.work_guid_coverage_summary, null);
        if (summary && typeof summary === "object" && Number(summary.key_count)) {
          group.work_guid_coverage_summary = summary;
        }
      });
      const statusRank = {
        DIFFERENCE: 0,
        REVIEW: 1,
        NO_USP_DUMP: 2,
        MATCH: 3,
      };
      return [...map.values()].sort((a, b) => {
        const rankDelta = (statusRank[a.complex_comparison_status] ?? 9)
          - (statusRank[b.complex_comparison_status] ?? 9);
        if (rankDelta) return rankDelta;
        const absA = Math.abs(Number(a.fact_complex_minus_avr_year || 0));
        const absB = Math.abs(Number(b.fact_complex_minus_avr_year || 0));
        if (absA !== absB) return absB - absA;
        return String(a.resident_name || "").localeCompare(String(b.resident_name || ""), "ru");
      });
    },
    complexOptions() {
      return this.groups.map((group) => ({
        label: `${group.resident_name} · SBS ${group.sbs_project_id}`,
        value: group.project_guid,
      }));
    },
    filteredGroups() {
      const search = this.filters.search.toLocaleLowerCase("ru");
      return this.groups.filter((group) => {
        if (this.filters.complex && group.project_guid !== this.filters.complex) return false;
        if (this.filters.status !== "ALL" && !this.groupHasStatus(group, this.filters.status)) {
          return false;
        }
        if (this.filters.onlyCases && group.complex_comparison_status === "MATCH") return false;
        if (!search) return true;
        const haystack = [
          group.resident_name,
          group.usp_project_name,
          group.project_guid,
          group.sbs_project_id,
          ...group.projects.flatMap((project) => [
            project.project_id,
            project.agreement_num,
            project.contract_guid,
            project.contractor_name,
          ]),
        ].filter(Boolean).join(" ").toLocaleLowerCase("ru");
        return haystack.includes(search);
      });
    },
    hiddenMatchCount() {
      if (!this.filters.onlyCases) return 0;
      return this.groups.filter((group) => group.complex_comparison_status === "MATCH").length;
    },
    hiddenOtherCount() {
      return Math.max(
        0,
        this.groups.length - this.filteredGroups.length - this.hiddenMatchCount,
      );
    },
    summary() {
      return {
        complexes: this.groups.length,
        projects: this.rows.length,
        differences: this.groups.filter(
          (item) => item.complex_comparison_status === "DIFFERENCE",
        ).length,
        review: this.groups.filter(
          (item) => item.complex_comparison_status === "REVIEW",
        ).length,
        matches: this.groups.filter(
          (item) => item.complex_comparison_status === "MATCH",
        ).length,
      };
    },
    filterQuery() {
      const query = { only: this.filters.onlyCases ? "1" : "0" };
      if (this.filters.complex) query.complex = this.filters.complex;
      if (this.filters.status && this.filters.status !== "ALL") {
        query.status = this.filters.status;
      }
      if (this.filters.search) query.search = this.filters.search;
      return query;
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.syncQuery();
      },
    },
  },
  created() {
    this.applyQuery(this.$route.query);
  },
  mounted() {
    this.loadReport();
  },
  methods: {
    applyQuery(query = {}) {
      this.syncingQuery = true;
      if (query.complex) this.filters.complex = query.complex;
      if (STATUS_VALUES.includes(query.status)) this.filters.status = query.status;
      if (typeof query.search === "string") this.filters.search = query.search;
      if (query.only === "0" || query.only === "false") this.filters.onlyCases = false;
      if (query.only === "1" || query.only === "true") this.filters.onlyCases = true;
      this.$nextTick(() => {
        this.syncingQuery = false;
        this.syncQuery();
      });
    },
    syncQuery() {
      if (this.syncingQuery) return;
      const next = this.filterQuery;
      const current = this.$route.query || {};
      const keys = new Set([...Object.keys(next), ...Object.keys(current)]);
      const same = [...keys].every((key) => String(next[key] || "") === String(current[key] || ""));
      if (same) return;
      this.$router.replace({ query: next }).catch(() => {});
    },
    async loadReport() {
      this.loading = true;
      this.errorText = "";
      try {
        const result = await fetchUspNdsAvrYearReport({ tolerance: 1 });
        this.rows = result.rows;
        this.meta = result.meta;
      } catch (error) {
        this.errorText = error?.response?.data?.message
          || "Не удалось загрузить отчёт. Проверьте, что функция отчёта установлена в sbs_db.";
      } finally {
        this.loading = false;
      }
    },
    coverageKey(group) {
      return group.project_guid || `sbs-${group.sbs_project_id}`;
    },
    parseJsonValue(raw, fallback) {
      if (raw == null || raw === "") return fallback;
      if (typeof raw !== "string") return raw;
      try {
        return JSON.parse(raw);
      } catch (error) {
        return fallback;
      }
    },
    normalizeCoverageList(raw) {
      const parsed = this.parseJsonValue(raw, []);
      return Array.isArray(parsed) ? parsed : [];
    },
    asIdList(value) {
      if (Array.isArray(value)) return value.map(String);
      if (value == null || value === "") return [];
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed.startsWith("[")) {
          const parsed = this.parseJsonValue(trimmed, null);
          return Array.isArray(parsed) ? parsed.map(String) : [trimmed];
        }
        if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
          return trimmed.slice(1, -1).split(",").map((item) => item.trim()).filter(Boolean);
        }
        return [trimmed];
      }
      return [String(value)];
    },
    coverageSearchHaystack(item) {
      return [
        item.work_guid,
        ...this.asIdList(item.project_ids),
        ...this.asIdList(item.avr_project_ids),
      ].join(" ").toLocaleLowerCase("ru");
    },
    coverageSummary(group) {
      const summary = this.parseJsonValue(group.work_guid_coverage_summary, null);
      if (!summary || !Number(summary.key_count)) return null;
      return summary;
    },
    coverageList(group) {
      return this.normalizeCoverageList(group.work_guid_coverage);
    },
    coverageTabOf(group) {
      const selected = this.coverageTab[this.coverageKey(group)];
      if (selected) return selected;
      const summary = this.parseJsonValue(group.work_guid_coverage_summary, {}) || {};
      return Number(summary.dump_without_avr_count) > 0 ? "holes" : "all";
    },
    setCoverageTab(group, tab) {
      this.coverageTab = { ...this.coverageTab, [this.coverageKey(group)]: tab };
    },
    coverageSearchOf(group) {
      return this.coverageSearch[this.coverageKey(group)] || "";
    },
    setCoverageSearch(group, value) {
      const key = this.coverageKey(group);
      this.coverageSearch = { ...this.coverageSearch, [key]: value || "" };
      if (value) {
        this.coverageTab = { ...this.coverageTab, [key]: "all" };
      }
    },
    setCoverageLimit(group, limit) {
      this.coverageLimit = { ...this.coverageLimit, [this.coverageKey(group)]: limit };
    },
    filteredCoverage(group) {
      const tab = this.coverageTabOf(group);
      const search = String(this.coverageSearchOf(group) || "").toLocaleLowerCase("ru");
      return this.coverageList(group).filter((item) => {
        if (tab === "holes" && !(item.in_dump && !item.has_signed_avr)) return false;
        if (tab === "ok" && !(item.in_dump && item.has_signed_avr)) return false;
        if (tab === "no_dump" && !(item.has_signed_avr && !item.in_dump)) return false;
        if (tab === "shared" && !item.is_shared) return false;
        if (!search) return true;
        return this.coverageSearchHaystack(item).includes(search);
      });
    },
    visibleCoverage(group) {
      const limit = this.coverageLimit[this.coverageKey(group)] || 80;
      return this.filteredCoverage(group).slice(0, limit);
    },
    money(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
      return new Intl.NumberFormat("ru-RU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value));
    },
    nullableMoney(value) {
      return value === null || value === undefined ? "На уровне ЖК" : `${this.money(value)} ₸`;
    },
    signedMoney(value) {
      const number = Number(value || 0);
      return `${number > 0 ? "+" : ""}${this.money(number)}`;
    },
    signedNullable(value) {
      if (value === null || value === undefined) return "На уровне ЖК";
      return `${this.signedMoney(value)} ₸`;
    },
    groupHasStatus(group, status) {
      if (group.complex_comparison_status === status) return true;
      return group.projects.some((project) => project.project_comparison_status === status);
    },
    deltaTone(value) {
      if (value === null || value === undefined) return "";
      return Math.abs(Number(value)) <= this.tolerance ? "ok" : "danger";
    },
    deltaClass(value) {
      if (value === null || value === undefined) return "";
      return Math.abs(Number(value)) <= this.tolerance ? "delta-ok" : "delta-danger";
    },
    methodLabel(code) {
      return METHOD_LABELS[code] || "Недостаточно данных";
    },
    contractLabel(project) {
      if (!project.agreement_num) return "Договор не найден";
      const date = project.agreement_date
        ? new Date(project.agreement_date).toLocaleDateString("ru-RU")
        : null;
      return `Договор ${project.agreement_num}${date ? ` от ${date}` : ""}`;
    },
    rateLabel(rates) {
      return Array.isArray(rates) ? rates.map((rate) => `${this.money(rate)}%`).join(" / ") : "—";
    },
    sharedProjectsLabel(details) {
      if (!Array.isArray(details)) return "";
      const ids = new Set();
      details.forEach((item) => (item.project_ids || []).forEach((id) => ids.add(id)));
      return ids.size ? `Проекты на общих ключах: ${[...ids].sort((a, b) => a - b).join(", ")}.` : "";
    },
    dateTime(value) {
      return value ? new Date(value).toLocaleString("ru-RU") : "—";
    },
    copyText(value) {
      if (!value) return;
      navigator.clipboard.writeText(value).catch(() => {});
    },
  },
};
</script>

<style scoped>
.vat-report {
  --ink: #172033;
  --muted: #687386;
  --line: #e6eaf0;
  max-width: 1680px;
  margin: 0 auto;
  padding: 24px;
  color: var(--ink);
}
.report-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 18px;
}
.report-hero h1 { margin: 2px 0 6px; font-size: 30px; line-height: 1.2; font-weight: 700; }
.report-hero p { max-width: 820px; margin: 0; color: var(--muted); line-height: 1.55; }
.eyebrow { color: #315fc4; font-size: 12px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
.hero-actions { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.help-btn { background: #fff; }
.filters {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
}
.filter-control { width: 260px; }
.filter-search { min-width: 260px; flex: 1; }
.filter-toggle { min-width: 220px; }
.filter-toggle-hint { margin: -4px 0 0 12px; color: var(--muted); font-size: 11px; line-height: 1.35; max-width: 220px; }
.summary-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; margin: 18px 0; }
.summary-card {
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(31, 45, 72, .04);
}
.summary-card span { display: block; color: var(--muted); font-size: 13px; }
.summary-card strong { display: block; margin-top: 8px; font-size: 28px; }
.summary-card--danger { border-left: 4px solid #c43d4b; }
.summary-card--warning { border-left: 4px solid #d89525; }
.summary-card--ok { border-left: 4px solid #2f9e64; }
.result-caption { margin: 4px 2px 10px; color: var(--muted); font-size: 13px; line-height: 1.45; }
.result-caption-note { margin-top: 4px; }
.complex-list { display: grid; gap: 14px; }
.complex-card { overflow: hidden; border: 1px solid var(--line); border-radius: 14px; background: #fff; }
.complex-title-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; width: 100%; }
.complex-title { font-size: 18px; font-weight: 700; }
.complex-subtitle { margin-top: 3px; color: var(--muted); font-size: 13px; }
.complex-body { padding: 4px 20px 20px; }
.guid-row { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; color: var(--muted); font-size: 12px; }
.guid-row code { padding: 3px 7px; border-radius: 5px; background: #f3f5f8; color: #35415a; }
.guid-copy-btn { margin-left: -2px; }
.endpoint-btn { min-height: 28px; padding: 0 10px; font-size: 12px; }
.endpoint-popup { padding: 12px 14px 10px; min-width: 320px; }
.endpoint-popup-label { margin-bottom: 6px; color: #687386; font-size: 11px; }
.endpoint-popup code {
  display: block;
  padding: 8px 10px;
  border-radius: 7px;
  background: #f3f5f8;
  color: #35415a;
  font-size: 12px;
  line-height: 1.45;
  word-break: break-all;
}
.endpoint-popup-actions { display: flex; gap: 4px; margin-top: 8px; }
.amount-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin: 16px 0 10px; }
.tmc-strip { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin: 0 0 16px; }
.tmc-strip :deep(.amount-tile) { background: #f7f8fa; }
.method-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  overflow: hidden;
  margin-bottom: 16px;
  border: 1px solid var(--line);
  border-radius: 10px;
}
.method-strip > div { padding: 11px 14px; border-right: 1px solid var(--line); cursor: help; }
.method-strip > div:last-child { border-right: 0; }
.method-strip span { display: block; color: var(--muted); font-size: 11px; }
.method-strip strong { display: block; margin-top: 4px; font-size: 13px; }
.closest-method { background: #faf7ef; }
.project-list { display: grid; gap: 10px; }
.project-card { padding: 15px; border: 1px solid var(--line); border-radius: 10px; background: #fff; }
.project-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.project-id { display: flex; align-items: center; gap: 8px; font-weight: 700; }
.project-status-col { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
.project-closest { color: var(--muted); font-size: 11px; }
.contract-line { margin-top: 4px; color: var(--muted); font-size: 12px; }
.project-methods { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 8px; margin-top: 13px; }
.project-methods > div { min-width: 0; padding: 9px; border-radius: 7px; background: #f7f8fa; cursor: help; }
.project-methods span { display: block; color: var(--muted); font-size: 10px; }
.project-methods b { display: block; margin-top: 3px; font-size: 12px; word-break: break-word; }
.project-tmc { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; margin-top: 8px; }
.project-tmc > div { min-width: 0; padding: 9px; border-radius: 7px; background: #f3f5f8; cursor: help; }
.project-tmc span { display: block; color: var(--muted); font-size: 10px; }
.project-tmc b { display: block; margin-top: 3px; font-size: 12px; word-break: break-word; }
.badges { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
.project-note {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
  padding: 9px 11px;
  border-radius: 7px;
  background: #f5f7fb;
  color: #536078;
  font-size: 12px;
}
.delta-danger { color: #b12636; }
.delta-ok { color: #147742; }
.error-banner { margin: 16px 0; background: #fff1f1; color: #9e2733; }
.coverage-block {
  margin: 0 0 16px;
  padding: 14px 16px 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #f8fafc;
}
.coverage-title { font-size: 15px; font-weight: 700; }
.coverage-sub { margin-top: 3px; color: var(--muted); font-size: 12px; line-height: 1.45; max-width: 760px; }
.coverage-tmc-note { margin: 0 0 10px; color: var(--muted); font-size: 12px; line-height: 1.45; }
.coverage-stats { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 8px; margin: 12px 0; }
.coverage-stat {
  display: block;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 9px;
  background: #fff;
  text-align: left;
  cursor: pointer;
}
.coverage-stat span { display: block; color: var(--muted); font-size: 11px; }
.coverage-stat strong { display: block; margin-top: 4px; font-size: 20px; }
.coverage-stat em { display: block; margin-top: 2px; color: var(--muted); font-size: 11px; font-style: normal; }
.coverage-stat.is-active { border-color: #315fc4; box-shadow: 0 0 0 1px #315fc4; }
.coverage-stat--warn.is-active { border-color: #d89525; box-shadow: 0 0 0 1px #d89525; }
.coverage-search { margin-bottom: 10px; }
.coverage-list { display: grid; gap: 8px; max-height: 420px; overflow: auto; }
.coverage-row { padding: 10px 12px; border: 1px solid var(--line); border-radius: 9px; background: #fff; }
.coverage-guid { display: flex; align-items: center; gap: 4px; }
.coverage-guid code { font-size: 12px; }
.coverage-flags { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 7px; }
.coverage-meta { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 7px; color: var(--muted); font-size: 12px; }
.coverage-empty, .coverage-more { padding: 10px 2px; color: var(--muted); font-size: 13px; }
.empty-state { padding: 54px 20px; text-align: center; color: var(--muted); }
.empty-state .q-icon { display: block; margin: 0 auto 10px; font-size: 36px; }
.help-card { width: min(720px, 92vw); max-height: 86vh; border-radius: 16px; }
.help-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 22px 16px;
}
.help-kicker { color: #315fc4; font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
.help-title { margin-top: 4px; font-size: 22px; font-weight: 700; }
.help-card-body { padding: 8px 22px 18px; overflow: auto; }
.help-card-body h2 { margin: 16px 0 8px; font-size: 15px; }
.help-card-body p, .help-card-body li { color: #445166; font-size: 14px; line-height: 1.55; }
.help-card-body ul { margin: 0; padding-left: 18px; }
.help-card-body li { margin: 0 0 8px; }
@media (max-width: 1200px) {
  .summary-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 1100px) {
  .coverage-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .amount-grid, .method-strip, .tmc-strip, .project-tmc { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .method-strip > div { border-bottom: 1px solid var(--line); }
  .project-methods { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .filters { flex-wrap: wrap; }
  .filter-search, .filter-toggle { flex-basis: 100%; }
}
@media (max-width: 700px) {
  .vat-report { padding: 14px; }
  .report-hero, .hero-actions { flex-direction: column; align-items: stretch; }
  .summary-grid, .amount-grid, .method-strip, .tmc-strip, .project-methods, .project-tmc, .coverage-stats { grid-template-columns: 1fr; }
  .filter-control, .filter-search { width: 100%; min-width: 0; }
  .complex-title-row, .project-header { align-items: flex-start; flex-direction: column; }
}
</style>
